// JAWS Suspension Kinematics — labels, hardpoint schemas, and defaults.
//
// The backend is the from-scratch JAWS suspension-kinematics solver
// (telekinesis-kinematics, a stateless Linux service) — NOT the old proprietary
// Windows binary, which was only ever operable via disqualified GUI automation.
//
// The browser NEVER calls the solver directly: every call goes through the
// `suspension-kinematics` edge function (auth + token billing + X-TK-Auth to
// the executor) via `supabase.functions.invoke` — CLAUDE.md rule C and the
// frontend → edge fn → microservice rule. The executor 401s any direct call.
// To point at a different solver instance, set the SHARK_URL secret on the
// edge function's environment (not a VITE_ var — the URL is server-side now).

export const SUSP_TYPE_LABELS: Record<string, string> = {
  'double-wishbone': 'Double Wishbone',
  macpherson: 'MacPherson Strut',
  'multi-link': 'Multi-Link',
  'solid-axle': 'Solid Axle',
};

// Hardpoint keys present for each suspension type.
// The shark runner validates these before dispatching.
export const HARDPOINTS_BY_TYPE: Record<string, string[]> = {
  'double-wishbone': [
    'ucaIF',
    'ucaIR',
    'ucaO',
    'lcaIF',
    'lcaIR',
    'lcaO',
    'trI',
    'trO',
    'wc',
    'spindle',
    'smB',
    'smW',
    'damBot',
    'damTop',
  ],
  macpherson: [
    'lcaIF',
    'lcaIR',
    'lcaO',
    'trI',
    'trO',
    'wc',
    'smB',
    'smW',
    'strutTop',
  ],
  // Multi-link: the solver's 5-link model — 5 body anchors (aN) + 5 upright
  // points (uN) + hub. Same keys the AC importer emits, so a manual preset and an
  // imported ML car render + solve identically.
  'multi-link': [
    'a1',
    'a2',
    'a3',
    'a4',
    'a5',
    'u1',
    'u2',
    'u3',
    'u4',
    'u5',
    'wc',
  ],
  'solid-axle': [
    'axleCenter',
    'panhardI',
    'panhardO',
    'trI',
    'trO',
    'smB',
    'smW',
  ],
};

export const HARDPOINT_LABELS: Record<string, string> = {
  ucaIF: 'UCA Inboard Front',
  ucaIR: 'UCA Inboard Rear',
  ucaO: 'UCA Outboard',
  lcaIF: 'LCA Inboard Front',
  lcaIR: 'LCA Inboard Rear',
  lcaO: 'LCA Outboard',
  trI: 'Tie Rod Inboard',
  trO: 'Tie Rod Outboard',
  wc: 'Wheel Center',
  spindle: 'Wheel Spindle (axle)',
  smB: 'Spring Mount (Body)',
  smW: 'Spring Mount (Wheel)',
  damTop: 'Damper (Body End)',
  damBot: 'Damper (Wishbone End)',
  strutTop: 'Strut Top Mount',
  traIB: 'Trailing Arm Inboard',
  traOB: 'Trailing Arm Outboard',
  a1: 'Link 1 Body',
  a2: 'Link 2 Body',
  a3: 'Link 3 Body',
  a4: 'Link 4 Body',
  a5: 'Link 5 Body (toe)',
  u1: 'Link 1 Upright',
  u2: 'Link 2 Upright',
  u3: 'Link 3 Upright',
  u4: 'Link 4 Upright',
  u5: 'Link 5 Upright (toe)',
  axleCenter: 'Axle Center',
  panhardI: 'Panhard Rod Inboard',
  panhardO: 'Panhard Rod Outboard',
};

// Default DWB hardpoints — a reference "Tutorial 2a" front suspension, in the
// solver convention (x = lateral/outboard+, y = longitudinal/fwd+,
// z = vertical/up+). Validated head-to-head against the reference kinematics
// oracle (camber/caster/kingpin/toe to <0.05°, roll centre to <5 mm). Tyre
// radius for this model is 225 mm
// (DEFAULT_TYRE_RADIUS_MM). The `spindle` point sets the static camber/toe.
export const DEFAULT_TYRE_RADIUS_MM = '225';
export const DEFAULT_HARDPOINTS_DWB: Record<string, [string, string, string]> =
  {
    lcaIF: ['91', '3819', '181'],
    lcaIR: ['92', '4179', '197'],
    lcaO: ['723.5', '4092', '167.1'],
    ucaIF: ['308', '4092.5', '385'],
    ucaIR: ['308', '4332', '369'],
    ucaO: ['698.5', '4107.5', '458.1'],
    trI: ['197', '4245.5', '230'],
    trO: ['668', '4214.5', '233'],
    wc: ['750', '4092.5', '313.1'],
    spindle: ['701', '4092.5', '311.817'],
    smB: ['486', '4180', '690'],
    smW: ['645', '4145', '210'],
    damBot: ['600', '4146.5', '203.6'],
    damTop: ['475', '4180', '593.6'],
  };

// Response shape from the `suspension-kinematics` edge function (which relays
// the solver's kinematics tables and optionally adds an LLM summary).
// rc_height / instant_center are null at geometric singularities — see
// KinematicsData in shared/types.ts.
export type SharkRunnerResponse = {
  kinematics?: {
    travel_mm: number[];
    camber_deg: number[];
    toe_mm: number[];
    caster_deg: number[];
    rc_height_mm: (number | null)[];
    instant_center_x?: (number | null)[];
    instant_center_z?: (number | null)[];
  };
  summary?: string;
  error?: string;
};
